import {
  fetchBlogCategories,
  fetchBlogEntry,
  generateBlogSlugs
} from '../../../services/contentfulPosts'
import * as React from 'react'
import type { AppStateType } from '../../../reducers/appstate'
import type { Dispatch } from 'redux'
import { useRouter, withRouter } from 'next/router'
import { connect } from 'react-redux'
import type {
  BlogPostType,
  BlogCategoryType,
  BlogSlugsType
} from '../../../services/contentfulPosts'
import styled from 'styled-components'
import { Container, config, Row, Col, Hidden } from 'react-awesome-styled-grid'
import {
  colors,
  CTAPrimary,
  GlobalStyle,
  H160,
  P16,
  H5
} from '@components/shared'
import TopNav from '@components/TopNav'
import Categories from '@components/blog/Categories'
import CategoryPillButton from '@components/blog/CategoryPillButton'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import { MARKS } from '@contentful/rich-text-types'
import type { Document as RichTextDocument } from '@contentful/rich-text-types'
import SocialShare from '@components/blog/SocialShare'
import ThreeHorizontalPosts from '@components/blog/ThreeHorizontalPosts'
import SectionTitle from '@components/blog/SectionTitle'
import Footer from '@components/Footer'
import HtmlHead from '@components/HtmlHead'
import TopNavMobile from '@components/TopNavMobile'
import { FadeInImage } from '@components/shared/FadeInImage'
import { signIn as openWebApp } from '../../../actions/navigationActions'
import type { UtmParamsType } from '../../../flowTypes'
import ConversionPaywallModal from '@components/subscribe/ConversionPaywallModal'
import { togglePromoModal } from '../../../actions/promoActions'
import BlogEmailCaptureModal from '@components/blog/BlogEmailCaptureModal'
import { ANNUAL_PRICE_STRING } from '../../../helpers/subscriptionHelpers'
import type { TrackPropsType } from '../../../models/Track'
import TrackTags from '@components/Tag/TrackTags'
import DriveToDownload from '@components/DriveToDownload'
import BackgroundGradient from '@components/BackgroundGradient'
import { addRomanceToTitle, getPageProps } from '../../../models/ThematicLandingPage'
import { BlogCarousel } from '@components/blog/BlogCarouselPreview'
import type { ThematicTagInfoType } from '../../../models/ThematicLandingPage'
import { getLibrary } from '@services/dipseaApi'

const IS_SUBSCRIPTION_MODAL = true

type PropsType = {
  categories: BlogCategoryType[],
  blogPost?: BlogPostType | null,
  thematicTag?: ThematicTagInfoType | null
}

type DispatchPropsType = {
  signIn: (utms: UtmParamsType) => void,
  toggleSubscriptionModal: () => void
}

const options = {
  renderNode: {
    [MARKS.CODE]: (node: any, children: any): React.ReactNode => {
      if (children[0] === '<br/>' || children[0] === '<br>') {
        return <br />
      }
      return <DangerZoneDiv dangerouslySetInnerHTML={{ __html: children }} />
    }
  }
}

const DangerZoneDiv = styled.div<any>`
  margin: 0 auto;
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  img {
    max-width: 100%;
  }
`

function FeaturedPreview (props: {
  signIn: (utm: UtmParamsType) => void,
  trackPreviews: TrackPropsType[],
  toggleSubscriptionModal: () => void
}): React.ReactNode {
  const router = useRouter()
  if (!props.trackPreviews.length) {
    return null
  }
  return (
    <FeaturedSectionWrapper>
      <FeaturedImagesContainer>
        {props.trackPreviews.map(
          (trackPreview: TrackPropsType, index: number): React.ReactNode => {
            return (
              <FeaturedStoryAncor
                key={`preview-${index}`}
                href={trackPreview.slug}
                target='_blank'
              >
                <FeaturedItem>
                  <FeaturedPreviewImg>
                    <FadeInImage
                      src={trackPreview.images.img500}
                      layout='fill'
                      alt={trackPreview.title}
                    />
                  </FeaturedPreviewImg>
                  <TextWrapper>
                    <FeaturedTitle>{trackPreview.title}</FeaturedTitle>
                    {trackPreview.series?.[0] && (<SeriesInfo>{trackPreview.series?.[0]?.episodeInfo} <span>{trackPreview.series[0].title}</span></SeriesInfo>)}
                    <FeaturedDescription>
                      {trackPreview.description}
                    </FeaturedDescription>
                  </TextWrapper>
                </FeaturedItem>
                <TrackTagsContainer><TrackTags tags={trackPreview.tags} {...({ id: trackPreview.id } as any)} /></TrackTagsContainer>
              </FeaturedStoryAncor>
            )
          }
        )}
      </FeaturedImagesContainer>
      <ButtonWrapper>
        {IS_SUBSCRIPTION_MODAL
          ? (
            <CTAPrimary
              hoverOpacity
              onClick={(): void => {
                router.push(`/discover?utm_campaign=blog&utm_medium=web&utm_source=dipsea`)
              }}
            >
              Discover more stories
            </CTAPrimary>
            )
          : (
            <CTAPrimary
              minWidth='132px'
              hoverOpacity
              onClick={(): void => {
                router.push(`/discover?utm_campaign=blog&utm_medium=web&utm_source=dipsea`)
              }}
            >
              Discover more stories
            </CTAPrimary>
            )}
      </ButtonWrapper>
    </FeaturedSectionWrapper>
  )
}

const TrackTagsContainer = styled.div<any>`
    margin-top: 10px;
    display: flex;
    width: 100%;
    ${(props: any): any => config(props).media.xs`
  `}
    ${(props: any): any => config(props).media.sm`
  `}
    ${(props: any): any => config(props).media.md`
     max-width: 350px;
     justify-content: center;
    *.track-tags {
      text-align: center;
    }
    `}
    ${(props: any): any => config(props).media.lg`
  `}
    ${(props: any): any => config(props).media.xl`
  `}
`
const SeriesInfo = styled.div<any>`
    margin-top: 8px;
    margin-bottom: 8px;
    color: ${colors.white};
    font-family: "Good Sans Regular";
    font-style: normal;
    font-weight: 400;
    line-height: 150%;
    text-decoration: none;
    letter-spacing: -0.3px;
    font-size: 13px;
    ${(props: any): any => config(props).media.md`
          font-size: 16px;
          margin-top: 16px;
    `}

    span {
        color: ${colors.tintOrange};
        text-decoration: underline;
    }
`

const FeaturedStoryAncor = styled.a<any>`
  text-decoration: none;
  padding-top: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid ${colors.white70};
  &:last-of-type {
      border: none
  }
  ${(props: any): any => config(props).media.md`
    border: none;
    margin-right: 10px;
  `};
    
`
const FeaturedSectionWrapper = styled.div<any>`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;

  * {
    box-sizing: border-box;
  }
  margin-bottom: 50px;
  margin-top: 50px;
  ${(props: any): any => config(props).media.md`
    justify-content: center;
  `};
`
const FeaturedItem = styled.div<any>`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  ${(props: any): any => config(props).media.md`
    flex-direction: column;
    justify-content: center;
    margin: 0 10px;
  `}
`
const FeaturedImagesContainer = styled.div<any>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  height: 100%;
  margin-bottom: 30px;
  ${(props: any): any => config(props).media.md`
    margin-bottom: 0px;
    flex-direction: row;
  `}
`
const FeaturedPreviewImg = styled.div<any>`
  display: block;
  position: relative;
  width: 128px;
  height: 128px;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
  ${(props: any): any => config(props).media.md`
    width: 217px;
    height: 217px;
  `}
  ${(props: any): any => config(props).media.lg`
    width: 237px;
    height: 237px;
  `}
  ${(props: any): any => config(props).media.xl`
    width: 279px;
    height: 279px;
  `}
`
const TextWrapper = styled.div<any>`
  margin-left: 20px;
  ${(props: any): any => config(props).media.md`
    margin-left: 0;
    text-align: center;
  `}
`
const ButtonWrapper = styled.div<any>`
  display: flex;
  width: 100%;
  button {
    flex: 1 0 auto;
  }
  ${(props: any): any => config(props).media.md`
    margin-top: 50px;
    max-width: 232px;
  `}
  ${(props: any): any => config(props).media.xl`
    max-width: 183x;
  `}
`
const FeaturedTitle = styled(H5)<any>`
    font-family: "Good Sans Regular";
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: 18px; /* 128.571% */
    letter-spacing: -0.3px;
    color: ${colors.white};
  ${(props: any): any => config(props).media.md`
    text-align: center;
    font-size: 18px;
    margin-top: 16px;
  `}
`
const FeaturedDescription = styled.div<any>`
  margin-bottom: 0 !important;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  font-family: "Good Sans Regular";
  font-size: 11px;
  font-style: normal;
  font-weight: 400;
  line-height: 15px; /* 136.364% */
  letter-spacing: -0.3px;
  color: ${colors.white};
  ${(props: any): any => config(props).media.md`
    display: none;
  `}
`

function Blog ({
  categories,
  blogPost,
  signIn,
  toggleSubscriptionModal,
  thematicTag
}: PropsType & DispatchPropsType): React.ReactNode {
  if (!blogPost) {
    return <div>loading</div>
  }
  let themeTitle
  let themeLogLine
  if (thematicTag) {
    if (thematicTag.title) themeTitle = addRomanceToTitle(thematicTag.title)
    if (thematicTag.logLine) themeLogLine = thematicTag.logLine
  }

  const isNewFormat = !!blogPost.blogContent1
  let content = documentToReactComponents(blogPost.blogContent as RichTextDocument, options)

  if (isNewFormat) {
    content = (
      <div>
        {blogPost.blogContent1 &&
          documentToReactComponents(blogPost.blogContent1 as RichTextDocument, options)}
        {blogPost.trackPreviews &&
          FeaturedPreview({
            signIn,
            trackPreviews: blogPost.trackPreviews,
            toggleSubscriptionModal
          })}
        {blogPost.blogContent2 &&
          documentToReactComponents(blogPost.blogContent2 as RichTextDocument, options)}
      </div>
    )
  }

  return (
    <>
      <GlobalStyle backgroundColor={colors.violet} />
      <HtmlHead
        title={blogPost.title}
        image={blogPost.postPhoto?.file?.url}
        description={blogPost.excerpt}
      />
      <BlogEmailCaptureModal />
      <BackgroundContainer>
        <BackgroundGradient
          quadrant={3}
          gradientColor={colors.violet}
          gradientPosition='bottom'
          overlayColor='magenta'
          parallax
        />

        <TopNav
          backgroundColor={colors.violet95}
          isAssetLoaded={true}
          isBlog={true}
        />
        <TopNavMobile
          backgroundColor={colors.violet95}
          isAssetLoaded={true}
          isBlog={true}
        />
        <Container>
          <Hidden xs={true} sm={true} md={true}>
            <SocialShareMenu>
              <SocialShare title={blogPost.title} />
            </SocialShareMenu>
          </Hidden>
          <Row>
            <Col
              xs={4}
              sm={8}
              lg={10}
              offset={{
                lg: 1,
                xl: 1
              }}
              justify={'center'}
            >
              <CategoryContainer>
                <Categories categories={categories} />
              </CategoryContainer>
              <Hidden xs={true} sm={true}>
                <Hr size={1} />
              </Hidden>
              <Row>
                <Col
                  xs={4}
                  sm={4}
                  lg={5}
                  offset={{
                    lg: 1,
                    xl: 1
                  }}
                  align={'center'}
                  justify={'center'}
                >
                  {blogPost.postPhoto && (
                    <PostImage src={blogPost.postPhoto.file.url} />
                  )}
                </Col>
                <Col xs={4} sm={4} md={4} lg={5} justify={'center'}>
                  <ContentContainer>
                    <PillContainer>
                      {(blogPost.categories || []).map(
                        (category: BlogCategoryType): React.ReactNode => (
                          <CategoryPillButton
                            category={category}
                            key={category.id}
                          />
                        )
                      )}
                    </PillContainer>
                    <TitleContainer>
                      <H160 color={colors.sand}>{blogPost.title}</H160>
                    </TitleContainer>
                    <AuthorContainer>
                      <P16 color={colors.sand}>
                        <b>{blogPost.author?.authorName}</b>
                        <span
                          style={{ marginLeft: '10px', marginRight: '10px' }}
                        >
                          |
                        </span>
                        {blogPost.date}
                      </P16>
                    </AuthorContainer>
                  </ContentContainer>
                </Col>
                <Hidden xs={true} sm={true}>
                  <Col xs={4} sm={8} lg={12}>
                    <Hr size={1} />
                  </Col>
                </Hidden>
              </Row>
            </Col>
          </Row>
        </Container>
      </BackgroundContainer>
      <Container>
        <Row>
          <Col
            xs={4}
            sm={8}
            lg={10}
            offset={{
              lg: 1,
              xl: 1
            }}
            justify={'center'}
          >
            {blogPost.thematicTag && !!themeTitle && !!themeLogLine && (
              <div id="blog-preview-carousel">
                <BlogCarousel isCoverflow isSmall themeLogLine={themeLogLine} thematicTag={blogPost.thematicTag} themeTitle={themeTitle}/>
              </div>
            )}
            <Row>
              <Col
                xs={4}
                sm={8}
                lg={10}
                xl={8}
                offset={{
                  xl: 2,
                  lg: 1
                }}
              >
                <PostBodyContainer>
                  {content}
                </PostBodyContainer>
              </Col>
            </Row>
            {blogPost.related && (
              <Row>
                <Col
                  xs={4}
                  sm={8}
                  lg={10}
                  offset={{
                    lg: 1,
                    xl: 1
                  }}
                >
                  <SectionTitle title={'Related'} />
                </Col>
                <Col
                  xs={4}
                  sm={8}
                  lg={10}
                  offset={{
                    lg: 1,
                    xl: 1
                  }}
                >
                  <ThreeHorizontalPosts
                    title={'Related Posts'}
                    blogs={blogPost.related}
                  />
                </Col>
              </Row>
            )}
          </Col>
        </Row>
      </Container>
      <Container>
        <Row align='center' justify='center'>
          <Col>
            <div>
              <DriveToDownload dontShowPhoneImage={true} />
            </div>
          </Col>
        </Row>
      </Container>

      <Footer
        backgroundColor={colors.midnightBlue}
        isPromoModal={IS_SUBSCRIPTION_MODAL}
      />

      {IS_SUBSCRIPTION_MODAL && (
        <ConversionPaywallModal
          subtype='annual'
          defaultSignIn
          subscriptionOfferCta='TRY 7 DAYS FREE'
          subscriptionOfferTitle='7 Day Free Trial'
          subscriptionOfferSubtitle={`Annual subscription of ${ANNUAL_PRICE_STRING} USD billed at trial end`}
          subscriptionOfferSubtext={`Your card will be charged a sum of ${ANNUAL_PRICE_STRING} USD in one week for the first year of unlimited access to Dipsea. You must cancel your subscription before the end of your free trial if you do not want to be charged. Your annual subscription will auto-renew for ${ANNUAL_PRICE_STRING} USD in exactly one year and each year thereafter until you cancel.`}
          isFullScreen
          pageSlugForPromoEvent={blogPost.slug}
        />
      )}
    </>
  )
}

type ContextType = {
  params: {
    slug: string
  }
}

export async function getStaticProps (
  context: ContextType
): Promise<{ props: PropsType }> {
  const [categories, blogPost, library] = await Promise.all([
    fetchBlogCategories(),
    fetchBlogEntry(context.params.slug),
    getLibrary()
  ])
  let thematicTag = null
  if (blogPost && blogPost?.thematicTag && blogPost?.thematicTag?.[0]) {
    thematicTag = getPageProps(blogPost.thematicTag[0], library)
  }

  return {
    props: {
      categories,
      blogPost,
      thematicTag
    }
  }
}

export async function getStaticPaths (): Promise<BlogSlugsType> {
  const paths = await generateBlogSlugs()
  return paths
}

const mapStateToProps = (state: AppStateType, ownProps: PropsType): any => {
  return {
    ...ownProps
  }
}

const mapDispatchToProps = (dispatch: Dispatch<any>): any => {
  return {
    signIn: (utms?: UtmParamsType): void => dispatch(openWebApp(utms)),
    toggleSubscriptionModal: (): void => dispatch(togglePromoModal())
  }
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Blog) as any) as any

const BackgroundContainer = styled.div<any>`
  position: relative;
`
const CategoryContainer = styled.div<any>`
  width: 100%;
  display: flex;
  justify-content: center;
  margin-top: 40px;
  ${(props: any): any => config(props).media.md`
    margin-top: 70px;
  `}
`
const AuthorContainer = styled.div<any>`
  margin-top: 25px;
`
const Hr = styled.hr<any>`
  height: 1px;
  background-color: ${colors.sand};
  border: none;
  width: 100%;
  margin-top: 25px;
  margin-bottom: 50px;
`
const ContentContainer = styled.div<any>`
  display: flex;
  flex-direction: column;
`
const PostImage = styled.img<any>`
  width: 100%;
  margin-bottom: 15px;
  margin-top: 33px;
  border-radius: 10px;

  ${(props: any): any => config(props).media.sm`
    max-width: 337px;
    margin-top: 0px;
  `}

  ${(props: any): any => config(props).media.lg`
    max-width: 492px;
  `}

  ${(props: any): any => config(props).media.xl`
    max-width: 562px;
  `}
`
const PillContainer = styled.div<any>`
  margin-bottom: 10px;

  ${(props: any): any => config(props).media.lg`
    margin-bottom: 20px;
  `}

  ${(props: any): any => config(props).media.xl`
    margin-bottom: 25px;
  `}
`
const TitleContainer = styled.div<any>`
  margin-bottom: 10px;

  ${(props: any): any => config(props).media.lg`
    margin-bottom: 15px;
  `}
`
const PostBodyContainer = styled.div<any>`
  h2 {
    color: ${colors.sand};
    font-family: 'Queens Condensed Light';
    font-style: normal;
    font-weight: 300;
    letter-spacing: 0;
    text-align: 'left';
    margin: 0;
    font-size: 36px;
    line-height: 43px;
    ${(props: any): any => config(props).media.xl`
      font-size: 50px;
      line-height: 60px;
    `}
    ${(props: any): any => config(props).media.lg`
      font-size: 45px;
      line-height: 49px;
    `}
  }

  h3 {
    color: ${colors.sand};
    font-family: 'Queens Condensed Light';
    font-style: normal;
    font-weight: 300;
    letter-spacing: 0;
    text-align: 'left';
    margin: 0;
    font-size: 26px;
    line-height: 33px;
    ${(props: any): any => config(props).media.xl`
      font-size: 40px;
      line-height: 50px;
    `}
    ${(props: any): any => config(props).media.lg`
      font-size: 35px;
      line-height: 39px;
    `}
  }

  p, code {
    color: ${colors.sand};
    font-family: 'Good Sans Light';
    font-style: normal;
    font-weight: 300;
    letter-spacing: 0;
    text-align: 'left';
    font-size: 16px;
    line-height: 24px;
    margin-bottom: 25px;

    ${(props: any): any => config(props).media.lg`
      font-size: 18px;
      line-height: 27px;
    `}
  }


  a {
    color: ${colors.orange};
  }

  ul {
    list-style: none;
    list-style-position: inside;
  }

  ul li::before {
    content: '\\2022';
    color: ${colors.sand}; /* Change the color */
    font-weight: bold; /* If you want it to be bold */
    display: inline-block; /* Needed to add space between the bullet and the text */
    width: 1em; /* Also needed for space (tweak if needed) */
    margin-left: -1em; /* Also needed for space (tweak if needed) */
    margin-right: 15px;
  }

  li {
    display: flex;
    margin-bottom: 15px;
    align-items: center;
    p {
      margin: 0;
    }
  }
`

const SocialShareMenu = styled.div<any>`
  position: fixed;
  left: 50px;
  top: 40% !important;
  bottom: auto;

  ${(props: any): any => config(props).media.xl`
     left: 100px;
  `}

  background: rgba(54, 0, 33, 0.6);
  backdrop-filter: blur(80px);
  border-radius: 10px;
  width: 74px;
  height: 192px;
`
